import React, { useState, useEffect, useRef } from "react";
import {
  Lightbulb,
  FileText,
  FileEdit,
  Cloud,
  Play,
  CheckCircle,
  Shield,
  Rocket,
  Globe,
  Loader2,
  ExternalLink,
  XCircle,
  LayoutGrid,
  Check,
} from "lucide-react";
import { useProjectStore } from "@/stores/projectStore";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import {
  UIPayload,
  ActivityEvent,
  Action,
  ThemeContext,
  PlanContext,
} from "@/lib/agent/payload-parser";
import { MarkdownRenderer } from "./MarkdownRenderer";
import { ConnectionSetupHITL } from "./ConnectionSetupHITL";
import { CacheTtlHITL } from "./cache-ttl-hitl";
import { MediaLibraryResultsHITL } from "./media-library-results-hitl";
import { OptisignsWriteConfirmHITL } from "./optisigns-write-confirm-hitl";
import { AskQuestionCard } from "./ask-question-card";
import { OptisignsAssetResultsHITL } from "./optisigns-asset-results-hitl";
import { CustomDomainConnectHITL } from "./CustomDomainConnectHITL";
import { ThemePreviewCard } from "@/components/editor/themes/components/theme-preview-card";
import type { ThemeContent } from "@/types/theme.types";
import { useAuth } from "@/contexts/AuthContext";
import { themesApi } from "@/lib/api/themes";
import { commitsApi } from "@/lib/api/commits";
import { previewMessage } from "@/lib/visual-editor/post-message";

interface ClaudeResponseCardProps {
  payload: UIPayload;
  markdown: string;
  messageId?: string; // For persisting clicked action state
  /** toolUseId of the producing tool (theme cards: stable apply identity, #54). */
  hitlToolUseId?: string;
  /** Timestamp of the source HITL message (theme cards: supersession check, #54). */
  hitlTimestamp?: Date;
  onRestore?: () => void;
  onViewCode?: () => void;
  onUpdatePreview?: () => void;
  onPreviewLatest?: () => void;
  onOpenFile?: (filePath: string) => void;
  // May be async: the OptiSigns destructive-write confirm awaits it so the card can show "Confirming…" and
  // re-enable its buttons if the write FAILS (nothing was written, so the user must be able to retry).
  onAction?: (actionId: string, messageId: string) => void | Promise<void>;
  clickedActions?: string[]; // IDs of buttons already clicked (persisted state)
  isStale?: boolean; // True if this message's actions are outdated (user sent new message after)
  className?: string;
  // Theme preview props
  workspaceId: string;
  previewIframe: HTMLIFrameElement | null;
  projectId: string;
  onEditTheme?: (theme: ThemeContent) => void;
  onThemeSaved?: (themeId: string) => void;
  onBrowseThemes?: () => void;
  // Connection setup HITL
  onConnectionCreated?: (
    connectionId: string,
    connectorType: string,
    connectionName: string,
  ) => void;
  // Cache TTL HITL — fires after the user submits a new TTL
  onCacheTtlSet?: (path: string, ttl: number) => void;
  // Media library HITL
  devContainerId?: string;
  previewUrl?: string;
  /** True while this message's agent turn is running — gates the media HITL submit. */
  isStreaming?: boolean;
}

// Render approve_plan HITL - styled card similar to OptiDev Cloud
function ApprovePlanHITL({
  action,
  isClicked,
  isStale,
  onAction,
  messageId,
  setLocalClickedActions,
}: {
  action: Action & { id: string; label: string };
  isClicked: boolean;
  isStale?: boolean;
  onAction?: (actionId: string, messageId: string) => void;
  messageId?: string;
  setLocalClickedActions: React.Dispatch<React.SetStateAction<string[]>>;
}) {
  if (isClicked) {
    return (
      <div className="bg-card border border-border rounded-lg p-3 w-fit">
        <div className="flex items-center gap-2 text-sm">
          <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-green-500">
            <Check className="h-3 w-3 text-white" strokeWidth={3} />
          </span>
          <span className="font-medium text-foreground">Plan approved</span>
        </div>
      </div>
    );
  }

  // If stale (user sent a new message after this plan), show "Plan Declined" state
  if (isStale) {
    return (
      <div className="bg-card border border-border rounded-lg p-3 w-fit">
        <div className="flex items-center gap-2 text-muted-foreground text-xs">
          <span>Plan declined</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-4 space-y-3 w-fit">
      <div className="flex items-center gap-2">
        <Play className="w-4 h-4 text-foreground" />
        <span className="text-sm font-medium">Ready to Execute</span>
      </div>

      <p className="text-xs text-muted-foreground">
        Review the plan above and approve to start implementation.
      </p>

      <Button
        variant="contrast"
        size="sm"
        onClick={() => {
          setLocalClickedActions((prev) => [...prev, action.id]);
          onAction?.(action.id, messageId || "");
        }}
        className="h-8 px-3 text-xs w-full !rounded-sm"
      >
        <Play className="w-3.5 h-3.5 mr-1.5" />
        {action.label}
      </Button>
    </div>
  );
}

// Render start_building HITL — just a single "Start Building" button.
function StartBuildingHITL({
  action,
  isClicked,
  onAction,
  messageId,
  setLocalClickedActions,
}: {
  action: Action & { id: string; label: string };
  planContext: PlanContext;
  isClicked: boolean;
  onAction?: (actionId: string, messageId: string) => void;
  messageId?: string;
  setLocalClickedActions: React.Dispatch<React.SetStateAction<string[]>>;
}) {
  if (isClicked) {
    return (
      <div className="flex items-center gap-2 text-sm">
        <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-green-500">
          <Check className="h-3 w-3 text-white" strokeWidth={3} />
        </span>
        <span className="font-medium text-foreground">Build started</span>
      </div>
    );
  }

  return (
    <Button
      variant="contrast"
      size="sm"
      onClick={() => {
        setLocalClickedActions((prev) => [...prev, action.id]);
        onAction?.(action.id, messageId || "");
      }}
      className="h-9 px-4 text-sm w-full max-w-md"
    >
      <Play className="w-4 h-4 mr-1.5" />
      {action.label}
    </Button>
  );
}

// Render activate_optidev_cloud HITL - styled card with feature highlights
function ActivateOptiDevCloudHITL({
  action,
  isClicked,
  onAction,
  messageId,
  setLocalClickedActions,
}: {
  action: Action & { id: string; label: string };
  isClicked: boolean;
  onAction?: (actionId: string, messageId: string) => void;
  messageId?: string;
  setLocalClickedActions: React.Dispatch<React.SetStateAction<string[]>>;
}) {
  // Once clicked, the message morphs into the activation progress card
  // (rendered by AgentMessage), so this card renders nothing.
  if (isClicked) {
    return null;
  }

  return (
    <div className="w-full max-w-md rounded-xl border border-border bg-card p-5">
      <div className="text-[15px] font-semibold tracking-tight text-foreground">
        Enable OptiDev Cloud
      </div>
      <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
        Adds your app's backend — a Postgres database, authentication, file
        storage, and edge functions — in one click.
      </p>
      <Button
        variant="contrast"
        size="sm"
        onClick={() => {
          setLocalClickedActions((prev) => [...prev, action.id]);
          onAction?.(action.id, messageId || "");
        }}
        className="mt-4 h-9 w-full rounded-md text-sm"
      >
        {action.label}
      </Button>
    </div>
  );
}

// Render publish_project HITL - styled card for publishing confirmation
// Uses projectStore for state synchronization with PublishDropdown
function PublishProjectHITL({
  action,
  isClicked,
  onAction,
  messageId,
  setLocalClickedActions,
  currentProjectId,
}: {
  action: Action & { id: string; label: string };
  isClicked: boolean;
  onAction?: (actionId: string, messageId: string) => void;
  messageId?: string;
  setLocalClickedActions: React.Dispatch<React.SetStateAction<string[]>>;
  currentProjectId?: string;
}) {
  const publishState = useProjectStore((state) => state.publishState);
  const [elapsedTime, setElapsedTime] = useState(0);

  // Only use store state if it belongs to the current project (prevents cross-project state leakage)
  const isCurrentProjectPublish = publishState.projectId === currentProjectId;

  // Track elapsed time when publishing
  useEffect(() => {
    if (
      isCurrentProjectPublish &&
      publishState.status !== "idle" &&
      publishState.status !== "success" &&
      publishState.status !== "failed"
    ) {
      const interval = setInterval(() => {
        if (publishState.startedAt) {
          setElapsedTime(
            Math.floor((Date.now() - publishState.startedAt) / 1000),
          );
        }
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setElapsedTime(0);
    }
  }, [isCurrentProjectPublish, publishState.status, publishState.startedAt]);

  // Get status text
  const getStatusText = () => {
    switch (publishState.status) {
      case "checking":
        return "Checking for changes...";
      case "queued":
        return "Starting build...";
      case "building":
        return `Building... ${elapsedTime}s`;
      case "deploying":
        return `Deploying... ${elapsedTime}s`;
      case "success":
        return "Published!";
      case "failed":
        return publishState.error || "Publish failed";
      default:
        return "";
    }
  };

  // Success state - show URL (only if this is the current project's publish)
  if (
    isCurrentProjectPublish &&
    publishState.status === "success" &&
    publishState.publicUrl
  ) {
    return (
      <div className="bg-gradient-to-br from-green-500/5 to-green-500/10 border border-green-500/20 rounded-xl p-4 w-fit max-w-[320px]">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-green-500/10 flex items-center justify-center flex-shrink-0">
            <CheckCircle className="w-5 h-5 text-green-500" />
          </div>
          <div className="space-y-1.5 min-w-0 flex-1">
            <p className="text-sm font-medium text-foreground">Published!</p>
            <a
              href={publishState.publicUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-green-400 hover:text-green-300 flex items-center gap-1.5 truncate"
            >
              <span className="truncate">{publishState.publicUrl}</span>
              <ExternalLink className="w-3 h-3 flex-shrink-0" />
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Failed state (only if this is the current project's publish)
  if (isCurrentProjectPublish && publishState.status === "failed") {
    return (
      <div className="bg-gradient-to-br from-red-500/5 to-red-500/10 border border-red-500/20 rounded-xl p-4 w-fit max-w-[320px]">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0">
            <XCircle className="w-5 h-5 text-red-500" />
          </div>
          <div className="space-y-1 min-w-0">
            <p className="text-sm font-medium text-foreground">
              Publish failed
            </p>
            <p className="text-xs text-muted-foreground">
              {publishState.error || "Something went wrong"}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // In-progress states - only when actually publishing (store-based, not persisted click)
  // After page refresh, isClicked may be true but store is 'idle' - don't show stuck progress
  // Note: 'success' and 'failed' cases are already handled by early returns above
  if (isCurrentProjectPublish && publishState.status !== "idle") {
    return (
      <div className="bg-muted/40 border border-border rounded-xl p-4 w-fit max-w-[320px]">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-background border border-border flex items-center justify-center flex-shrink-0">
            <Loader2 className="w-5 h-5 text-foreground animate-spin" />
          </div>
          <div className="space-y-1.5 min-w-0">
            <p className="text-sm font-medium text-foreground">
              {getStatusText()}
            </p>
            <div className="flex items-center gap-2">
              <div className="h-1.5 flex-1 bg-border rounded-full overflow-hidden min-w-[120px]">
                <div
                  className="h-full bg-foreground rounded-full transition-all duration-500"
                  style={{
                    width:
                      publishState.status === "queued"
                        ? "15%"
                        : publishState.status === "building"
                          ? "50%"
                          : publishState.status === "deploying"
                            ? "85%"
                            : "0%",
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Initial state - show publish button
  return (
    <div className="bg-muted/40 border border-border rounded-xl p-4 w-fit max-w-[320px]">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-background border border-border flex items-center justify-center flex-shrink-0">
          <Globe className="w-5 h-5 text-foreground" />
        </div>
        <div className="space-y-1 min-w-0">
          <p className="text-sm font-medium text-foreground">Share your app</p>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Get a public URL anyone can access
          </p>
        </div>
      </div>

      <Button
        variant="contrast"
        size="sm"
        disabled={isClicked}
        onClick={() => {
          setLocalClickedActions((prev) => [...prev, action.id]);
          onAction?.(action.id, messageId || "");
        }}
        className="h-9 px-4 text-sm w-full mt-4 rounded-md font-medium"
      >
        <Rocket className="w-4 h-4 mr-2" />
        Publish
      </Button>
    </div>
  );
}

// Render theme_approval HITL - uses ThemePreviewCard component
function ThemeApprovalHITL({
  themeContext,
  onAction,
  messageId,
  hitlToolUseId,
  hitlTimestamp,
  workspaceId,
  previewIframe,
  projectId,
  devContainerId,
  onEditTheme,
  onThemeSaved,
  onBrowseThemes,
}: {
  themeContext: ThemeContext;
  onAction?: (actionId: string, messageId: string) => void;
  messageId?: string;
  hitlToolUseId?: string;
  hitlTimestamp?: Date;
  workspaceId?: string;
  previewIframe?: HTMLIFrameElement | null;
  projectId?: string;
  devContainerId?: string;
  onEditTheme?: (theme: ThemeContent) => void;
  onThemeSaved?: (themeId: string) => void;
  onBrowseThemes?: () => void;
}) {
  // Transform ThemeContext to ThemeContent format for ThemePreviewCard
  const theme = {
    name: themeContext.name,
    type: "registry:theme",
    cssVars: JSON.parse(themeContext.content).cssVars,
    colors: themeContext.colors,
  };

  // Only render if required props are provided
  if (!workspaceId || !projectId) {
    return (
      <div className="w-full max-w-2xl p-4 bg-card border border-border rounded-lg">
        <p className="text-sm text-muted-foreground">
          Unable to display theme preview: missing required project information
        </p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-2xl overflow-hidden">
      <ThemePreviewCard
        theme={theme}
        workspaceId={workspaceId}
        previewIframe={previewIframe || null}
        projectId={projectId}
        devContainerId={devContainerId}
        onEditTheme={onEditTheme}
        onThemeSaved={onThemeSaved}
        onBrowseThemes={onBrowseThemes}
        autoApply={themeContext.autoApply === true}
        messageId={messageId}
        applySourceKey={hitlToolUseId ?? messageId}
        cardTimestamp={
          hitlTimestamp instanceof Date
            ? hitlTimestamp.toISOString()
            : hitlTimestamp
        }
      />
    </div>
  );
}

// Render theme_gallery HITL - lists existing themes, mirrors the bottom half of ThemePreviewCard
interface ThemeGalleryItem {
  id: string;
  themeId: string;
  name: string;
  colors: string[];
  content: string; // stringified JSON — used for instant iframe postMessage preview
  type: "built-in" | "custom";
}

function ThemeGalleryHITL({
  workspaceId,
  projectId,
  previewIframe,
}: {
  workspaceId?: string;
  projectId?: string;
  previewIframe?: HTMLIFrameElement | null;
}) {
  const { getIdToken } = useAuth();
  const [themes, setThemes] = useState<ThemeGalleryItem[] | null>(null);
  const [applyingId, setApplyingId] = useState<string | null>(null);
  const [appliedId, setAppliedId] = useState<string | null>(null);

  // Mouse drag-to-scroll for the horizontal theme row (mirrors ThemePreviewCard).
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, scrollLeft: 0 });
  const hasDraggedRef = useRef(false);

  // Auto-scroll the applied theme into view once, so the user opens the
  // gallery already centered on whatever theme is currently applied.
  const appliedItemRef = useRef<HTMLButtonElement | null>(null);
  const didAutoScrollRef = useRef(false);

  // Debounced auto-commit: only commit the theme the user "settles" on.
  // Each apply resets a 5s timer; when the timer fires, commit the most-recent theme.
  const commitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingCommitNameRef = useRef<string | null>(null);

  useEffect(() => {
    if (!workspaceId || !projectId) return;
    let cancelled = false;
    (async () => {
      const token = await getIdToken();
      if (!token || cancelled) return;
      try {
        const [list, current] = await Promise.all([
          themesApi.listThemes(token, workspaceId),
          themesApi.getProjectTheme(token, projectId).catch(() => null),
        ]);
        if (cancelled) return;
        setThemes(list);
        setAppliedId(current?.themeId ?? "optidev");
      } catch {
        if (!cancelled) setThemes([]);
      }
    })();
    return () => {
      cancelled = true;
      // Flush any pending commit on unmount so changes aren't lost if user leaves.
      if (commitTimerRef.current) {
        clearTimeout(commitTimerRef.current);
        const name = pendingCommitNameRef.current;
        commitTimerRef.current = null;
        pendingCommitNameRef.current = null;
        if (name && projectId) {
          commitsApi
            .createCommit(projectId, `Applied theme: ${name}`)
            .catch(() => {});
        }
      }
    };
  }, [getIdToken, workspaceId, projectId]);

  // Center the applied theme in the scroll row on first render (horizontal
  // only — manual scrollLeft instead of scrollIntoView so the surrounding
  // chat doesn't jump). Fires once per mount, after themes + appliedId resolve.
  useEffect(() => {
    if (didAutoScrollRef.current) return;
    if (!themes || themes.length === 0 || !appliedId) return;
    const container = scrollContainerRef.current;
    const el = appliedItemRef.current;
    if (!container || !el) return;
    didAutoScrollRef.current = true;
    const cRect = container.getBoundingClientRect();
    const eRect = el.getBoundingClientRect();
    const delta =
      eRect.left - cRect.left - (container.clientWidth - el.clientWidth) / 2;
    container.scrollLeft += delta;
  }, [themes, appliedId]);

  const scheduleCommit = (themeName: string) => {
    if (!projectId) return;
    // Reset timer on each apply so rapid experimentation only commits the final choice.
    if (commitTimerRef.current) clearTimeout(commitTimerRef.current);
    pendingCommitNameRef.current = themeName;
    commitTimerRef.current = setTimeout(() => {
      const name = pendingCommitNameRef.current;
      commitTimerRef.current = null;
      pendingCommitNameRef.current = null;
      if (!name) return;
      commitsApi.createCommit(projectId, `Applied theme: ${name}`).catch(() => {
        // Don't fail the UI on commit errors; they're non-critical.
      });
    }, 5000);
  };

  const handleApply = async (theme: ThemeGalleryItem) => {
    if (!projectId || applyingId || theme.themeId === appliedId) return;

    // Instant visual feedback: inject CSS vars into the iframe via postMessage
    // before the API round-trip. The API persist + Vite HMR will follow and
    // overwrite consistently; no visible flash because the values match.
    if (previewIframe && theme.content) {
      try {
        previewMessage.updateTheme(previewIframe, theme.themeId, theme.content);
      } catch {
        // Non-critical: even without instant preview, the API path still applies.
      }
    }

    // Optimistically mark this theme as applied so the UI updates immediately.
    setAppliedId(theme.themeId);
    setApplyingId(theme.themeId);

    const token = await getIdToken();
    if (!token) {
      setApplyingId(null);
      return;
    }
    try {
      await themesApi.applyTheme(token, projectId, theme.themeId);
      scheduleCommit(theme.name);
    } finally {
      setApplyingId(null);
    }
  };

  // Mouse drag-to-scroll handlers for the horizontal theme row.
  const handleMouseDown = (e: React.MouseEvent) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    setIsDragging(true);
    hasDraggedRef.current = false;
    dragStartRef.current = { x: e.pageX, scrollLeft: container.scrollLeft };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    e.preventDefault();
    const dx = e.pageX - dragStartRef.current.x;
    if (Math.abs(dx) > 3) hasDraggedRef.current = true;
    container.scrollLeft = dragStartRef.current.scrollLeft - dx;
  };

  const handleMouseUp = () => setIsDragging(false);
  const handleMouseLeave = () => setIsDragging(false);

  if (!workspaceId || !projectId) {
    return (
      <div className="w-full max-w-2xl p-4 bg-card border border-border rounded-lg">
        <p className="text-sm text-muted-foreground">
          Unable to display theme gallery: missing project information
        </p>
      </div>
    );
  }

  return (
    <Card className="overflow-hidden border border-border/60 shadow-sm bg-card w-full max-w-2xl">
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <span className="text-xs font-medium text-muted-foreground">
          Pick a theme of your choosing
        </span>
        {themes && themes.length > 0 && (
          <span className="text-[10px] text-muted-foreground/60">
            {themes.length} themes
          </span>
        )}
      </div>

      {/* Theme library - horizontal scroll, click to apply */}
      <div className="border-t border-border/40 bg-muted/30 px-4 py-3">
        {themes === null ? (
          <div className="flex items-center gap-2 py-4">
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
            <span className="text-xs text-muted-foreground">
              Loading themes...
            </span>
          </div>
        ) : themes.length === 0 ? (
          <div className="text-center py-4 text-xs text-muted-foreground">
            No themes available
          </div>
        ) : (
          <div className="grid grid-cols-1">
            <div
              ref={scrollContainerRef}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
              className={cn(
                "flex overflow-x-auto pb-2 pt-1 px-0.5 gap-3 select-none scrollbar-thin scrollbar-thumb-muted-foreground/20 scrollbar-track-transparent",
                isDragging ? "cursor-grabbing" : "cursor-grab",
              )}
            >
              {themes.map((t) => {
                const isApplied = appliedId === t.themeId;
                const isApplying = applyingId === t.themeId;
                return (
                  <button
                    key={t.id}
                    ref={isApplied ? appliedItemRef : undefined}
                    onClick={() => {
                      if (hasDraggedRef.current) return;
                      handleApply(t);
                    }}
                    disabled={!!applyingId}
                    title={isApplied ? `${t.name} (current)` : t.name}
                    className={cn(
                      "flex flex-col items-center gap-1 p-2 rounded-lg transition-all flex-shrink-0 w-[72px] hover:bg-accent/80 disabled:opacity-60 disabled:cursor-not-allowed",
                      // Clickable swatches show a pointer, not the row's grab hand;
                      // while dragging the row, keep the grabbing cursor everywhere.
                      isDragging
                        ? "cursor-grabbing"
                        : isApplied
                          ? "cursor-default"
                          : "cursor-pointer",
                      isApplied
                        ? "bg-accent ring-2 ring-foreground"
                        : "bg-background",
                    )}
                  >
                    <div className="relative">
                      <div className="grid grid-cols-2 w-10 h-10 rounded-md overflow-hidden">
                        {(t.colors || []).slice(0, 4).map((c, i) => (
                          <div key={i} style={{ backgroundColor: c }} />
                        ))}
                      </div>
                      {isApplying ? (
                        <div className="absolute inset-0 flex items-center justify-center bg-background/60 rounded-md">
                          <Loader2 className="w-3.5 h-3.5 animate-spin text-foreground" />
                        </div>
                      ) : isApplied ? (
                        <div className="absolute -top-1 -right-1 w-4 h-4 bg-foreground rounded-full flex items-center justify-center">
                          <Check className="w-2.5 h-2.5 text-background" />
                        </div>
                      ) : null}
                    </div>
                    <span
                      className={cn(
                        "text-[10px] font-medium truncate w-full text-center",
                        isApplied ? "text-foreground" : "text-muted-foreground",
                      )}
                    >
                      {t.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

// Render enable_cross_project_sharing HITL
function EnableCrossProjectSharingHITL({
  action,
  isClicked,
  onAction,
  messageId,
  setLocalClickedActions,
}: {
  action: Action & { id: string; label: string };
  isClicked: boolean;
  onAction?: (actionId: string, messageId: string) => void;
  messageId?: string;
  setLocalClickedActions: React.Dispatch<React.SetStateAction<string[]>>;
}) {
  if (isClicked) {
    return (
      <div className="bg-card border border-border rounded-lg p-3 w-fit">
        <div className="flex items-center gap-2 text-sm">
          <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-green-500">
            <Check className="h-3 w-3 text-white" strokeWidth={3} />
          </span>
          <span className="font-medium text-foreground">
            Cross-project sharing enabled
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-4 space-y-3 w-fit max-w-[320px]">
      <div className="flex items-center gap-2">
        <LayoutGrid className="w-4 h-4 text-foreground" />
        <span className="text-sm font-medium">Cross-Project Sharing</span>
      </div>

      <p className="text-xs text-muted-foreground">
        Enable to read files from other projects in your workspace.
      </p>

      <div className="flex gap-2">
        <Button
          variant="contrast"
          size="sm"
          onClick={() => {
            setLocalClickedActions((prev) => [...prev, action.id]);
            onAction?.(action.id, messageId || "");
          }}
          className="h-8 px-3 text-xs flex-1 !rounded-sm"
        >
          <LayoutGrid className="w-3.5 h-3.5 mr-1.5" />
          Enable
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            // Navigate to project settings
            const settingsBtn = document.querySelector(
              "[data-settings-trigger]",
            ) as HTMLButtonElement;
            if (settingsBtn) settingsBtn.click();
          }}
          className="h-8 px-3 text-xs !rounded-sm"
        >
          Settings
        </Button>
      </div>
    </div>
  );
}

// Render cross_project_workspace_disabled HITL — needs admin to change workspace settings
function CrossProjectWorkspaceDisabledHITL({
  action,
  isClicked,
  onAction,
  messageId,
  setLocalClickedActions,
}: {
  action: Action & { id: string; label: string };
  isClicked: boolean;
  onAction?: (actionId: string, messageId: string) => void;
  messageId?: string;
  setLocalClickedActions: React.Dispatch<React.SetStateAction<string[]>>;
}) {
  if (isClicked) {
    return (
      <div className="bg-card border border-border rounded-lg p-3 w-fit">
        <div className="flex items-center gap-2 text-muted-foreground text-xs">
          <Shield className="w-3.5 h-3.5 text-amber-500" />
          <span>Redirected to workspace settings</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-4 space-y-3 w-fit max-w-[320px]">
      <div className="flex items-center gap-2">
        <Shield className="w-4 h-4 text-amber-500" />
        <span className="text-sm font-medium">Workspace Sharing Disabled</span>
      </div>

      <p className="text-xs text-muted-foreground">
        Cross-project sharing is disabled at the workspace level. A workspace
        admin needs to enable it in Settings.
      </p>

      <Button
        variant="outline"
        size="sm"
        onClick={() => {
          setLocalClickedActions((prev) => [...prev, action.id]);
          onAction?.(action.id, messageId || "");
        }}
        className="h-8 px-3 text-xs w-full !rounded-sm"
      >
        <Shield className="w-3.5 h-3.5 mr-1.5" />
        {action.label}
      </Button>
    </div>
  );
}

export function ClaudeResponseCard({
  payload,
  markdown,
  messageId,
  hitlToolUseId,
  hitlTimestamp,
  onRestore,
  onViewCode,
  onUpdatePreview,
  onPreviewLatest,
  onOpenFile,
  onAction,
  clickedActions = [],
  isStale = false,
  className,
  workspaceId,
  previewIframe,
  projectId,
  onEditTheme,
  onThemeSaved,
  onBrowseThemes,
  onConnectionCreated,
  onCacheTtlSet,
  devContainerId,
  previewUrl,
  isStreaming = false,
}: ClaudeResponseCardProps) {
  const [showAllActivity, setShowAllActivity] = useState(false);
  const [showAllChanges, setShowAllChanges] = useState(false);
  const [localClickedActions, setLocalClickedActions] = useState<string[]>([]);
  // The OptiSigns confirm POST is in flight. Local to the card (not the parent) so a failed write simply
  // re-enables these buttons — nothing was written, so retrying must stay possible.
  const [isConfirmSubmitting, setIsConfirmSubmitting] = useState(false);

  // Check for HITL type and render specialized components
  if (payload.hitl_type === "approve_plan") {
    const action = payload.actions.find(
      (a): a is Action & { id: string; label: string } =>
        a.id === "approve_plan" && Boolean(a.label),
    );
    if (action) {
      const isClicked =
        localClickedActions.includes(action.id) ||
        clickedActions.includes(action.id);
      return (
        <div className={className}>
          <ApprovePlanHITL
            action={action}
            isClicked={isClicked}
            isStale={isStale}
            onAction={onAction}
            messageId={messageId}
            setLocalClickedActions={setLocalClickedActions}
          />
        </div>
      );
    }
  }

  if (payload.hitl_type === "start_building" && payload.planContext) {
    const action = payload.actions.find(
      (a): a is Action & { id: string; label: string } =>
        a.id === "start_building" && Boolean(a.label),
    );
    if (action) {
      const isClicked =
        localClickedActions.includes(action.id) ||
        clickedActions.includes(action.id);
      return (
        <div className={className}>
          <StartBuildingHITL
            action={action}
            planContext={payload.planContext}
            isClicked={isClicked}
            onAction={onAction}
            messageId={messageId}
            setLocalClickedActions={setLocalClickedActions}
          />
        </div>
      );
    }
  }

  if (payload.hitl_type === "activate_optidev_cloud") {
    const action = payload.actions.find(
      (a): a is Action & { id: string; label: string } =>
        a.id === "activate_optidev_cloud" && Boolean(a.label),
    );
    if (action) {
      const isClicked =
        localClickedActions.includes(action.id) ||
        clickedActions.includes(action.id);
      return (
        <div className={className}>
          <ActivateOptiDevCloudHITL
            action={action}
            isClicked={isClicked}
            onAction={onAction}
            messageId={messageId}
            setLocalClickedActions={setLocalClickedActions}
          />
        </div>
      );
    }
  }

  if (payload.hitl_type === "publish_project") {
    const action = payload.actions.find(
      (a): a is Action & { id: string; label: string } =>
        a.id === "publish_project" && Boolean(a.label),
    );
    if (action) {
      const isClicked =
        localClickedActions.includes(action.id) ||
        clickedActions.includes(action.id);
      return (
        <div className={className}>
          <PublishProjectHITL
            action={action}
            isClicked={isClicked}
            onAction={onAction}
            messageId={messageId}
            setLocalClickedActions={setLocalClickedActions}
            currentProjectId={projectId}
          />
        </div>
      );
    }
  }

  if (payload.hitl_type === "enable_cross_project_sharing") {
    const action = payload.actions.find(
      (a): a is Action & { id: string; label: string } =>
        a.id === "enable_cross_project_sharing" && Boolean(a.label),
    );
    if (action) {
      const isClicked =
        localClickedActions.includes(action.id) ||
        clickedActions.includes(action.id);
      return (
        <div className={className}>
          <EnableCrossProjectSharingHITL
            action={action}
            isClicked={isClicked}
            onAction={onAction}
            messageId={messageId}
            setLocalClickedActions={setLocalClickedActions}
          />
        </div>
      );
    }
  }

  if (payload.hitl_type === "cross_project_workspace_disabled") {
    const action = payload.actions.find(
      (a): a is Action & { id: string; label: string } =>
        a.id === "go_to_workspace_settings" && Boolean(a.label),
    );
    if (action) {
      const isClicked =
        localClickedActions.includes(action.id) ||
        clickedActions.includes(action.id);
      return (
        <div className={className}>
          <CrossProjectWorkspaceDisabledHITL
            action={action}
            isClicked={isClicked}
            onAction={onAction}
            messageId={messageId}
            setLocalClickedActions={setLocalClickedActions}
          />
        </div>
      );
    }
  }

  if (payload.hitl_type === "setup_connection" && payload.connectorType) {
    const action = payload.actions.find(
      (a): a is Action & { id: string; label: string } =>
        a.id === "setup_connection" && Boolean(a.label),
    );
    const isConnectionClicked = action
      ? localClickedActions.includes(action.id) ||
        clickedActions.includes(action.id)
      : false;
    const cancelActionId = "setup_connection_cancelled";
    const isConnectionCancelled =
      localClickedActions.includes(cancelActionId) ||
      clickedActions.includes(cancelActionId);
    return (
      <div className={className}>
        <ConnectionSetupHITL
          connectorType={payload.connectorType}
          isClicked={isConnectionClicked}
          isCancelled={isConnectionCancelled}
          isStale={isStale}
          onConnectionCreated={(
            connectionId,
            connectorType,
            connectionName,
          ) => {
            if (action) {
              setLocalClickedActions((prev) => [...prev, action.id]);
              onAction?.(action.id, messageId || "");
            }
            onConnectionCreated?.(connectionId, connectorType, connectionName);
          }}
          onCancelled={() => {
            setLocalClickedActions((prev) => [...prev, cancelActionId]);
            onAction?.(cancelActionId, messageId || "");
          }}
        />
      </div>
    );
  }

  if (payload.hitl_type === "cache_ttl" && payload.cacheTtlContext) {
    const action = payload.actions.find(
      (a): a is Action & { id: string; label: string } =>
        a.id === "submit_cache_ttl" && Boolean(a.label),
    );
    const isSubmitted = action
      ? localClickedActions.includes(action.id) ||
        clickedActions.includes(action.id)
      : false;
    const cancelActionId = "cache_ttl_cancelled";
    const isCacheTtlCancelled =
      localClickedActions.includes(cancelActionId) ||
      clickedActions.includes(cancelActionId);
    return (
      <div className={className}>
        <CacheTtlHITL
          projectId={projectId}
          endpointPath={payload.cacheTtlContext.endpointPath}
          requestedTtl={payload.cacheTtlContext.requestedTtl}
          isClicked={isSubmitted}
          isCancelled={isCacheTtlCancelled}
          isStale={isStale}
          onSubmitted={(path, ttl) => {
            if (action) {
              setLocalClickedActions((prev) => [...prev, action.id]);
              onAction?.(action.id, messageId || "");
            }
            onCacheTtlSet?.(path, ttl);
          }}
          onCancelled={() => {
            setLocalClickedActions((prev) => [...prev, cancelActionId]);
            onAction?.(cancelActionId, messageId || "");
          }}
        />
      </div>
    );
  }

  if (payload.hitl_type === "theme_approval" && payload.themeContext) {
    return (
      <div className={className}>
        <ThemeApprovalHITL
          themeContext={payload.themeContext}
          onAction={onAction}
          messageId={messageId}
          hitlToolUseId={hitlToolUseId}
          hitlTimestamp={hitlTimestamp}
          workspaceId={workspaceId}
          previewIframe={previewIframe}
          projectId={projectId}
          devContainerId={devContainerId}
          onEditTheme={onEditTheme}
          onThemeSaved={onThemeSaved}
          onBrowseThemes={onBrowseThemes}
        />
      </div>
    );
  }

  if (payload.hitl_type === "theme_gallery") {
    return (
      <div className={className}>
        <ThemeGalleryHITL
          workspaceId={workspaceId}
          projectId={projectId}
          previewIframe={previewIframe}
        />
      </div>
    );
  }
  console.log(
    "[ClaudeResponseCard] Rendering standard response card with payload:",
    payload,
  );
  if (payload.hitl_type === "ask_user_question" && payload.questions?.length) {
    return (
      <div className={className}>
        <AskQuestionCard
          requestId={payload.requestId || ""}
          questions={payload.questions}
          summary={payload.summary}
          onAction={onAction}
          messageId={messageId}
          clickedActions={clickedActions}
          isStale={isStale}
        />
      </div>
    );
  }

  if (payload.hitl_type === "media_library_results" && payload.mediaResults) {
    return (
      <div className={className}>
        <MediaLibraryResultsHITL
          mediaType={payload.mediaType || "image"}
          provider={payload.provider || "all"}
          mediaResults={payload.mediaResults}
          groups={payload.groups}
          summary={payload.summary || ""}
          onAction={onAction}
          messageId={messageId}
          isStreaming={isStreaming}
        />
      </div>
    );
  }

  if (payload.hitl_type === "optisigns_asset_results") {
    return (
      <div className={className}>
        <OptisignsAssetResultsHITL
          projectId={projectId}
          path={payload.optisignsPath}
          summary={payload.summary || ""}
          onAddToMessage={(refs) =>
            onAction?.(
              `add_optisigns_assets:${JSON.stringify(refs)}`,
              messageId || "",
            )
          }
        />
      </div>
    );
  }

  if (payload.hitl_type === "custom_domain" && payload.customDomain) {
    return (
      <div className={className}>
        <CustomDomainConnectHITL
          domain={payload.customDomain}
          projectId={projectId}
        />
      </div>
    );
  }

  if (
    payload.hitl_type === "optisigns_write_confirm" &&
    payload.optisignsWriteConfirm
  ) {
    const confirm = payload.optisignsWriteConfirm;
    const confirmActionId = "optisigns_write_confirmed";
    const cancelActionId = "optisigns_write_cancelled";
    const isConfirmed =
      localClickedActions.includes(confirmActionId) ||
      clickedActions.includes(confirmActionId);
    const isCancelled =
      localClickedActions.includes(cancelActionId) ||
      clickedActions.includes(cancelActionId);
    return (
      <div className={className}>
        <OptisignsWriteConfirmHITL
          summary={payload.summary || ""}
          confirm={confirm}
          isConfirmed={isConfirmed}
          isCancelled={isCancelled}
          isStale={isStale}
          isSubmitting={isConfirmSubmitting}
          onConfirm={async () => {
            // Redeem the pending op. Only the `opId` travels: the parent POSTs it to Kaze, which
            // authenticates this person and mints an approval the model cannot forge. We deliberately do NOT
            // re-invoke the tool with `confirmed: true` any more — that flag was model-settable, which is how
            // the human step got skipped and production really lost three assets and a folder.
            //
            // The clicked-marker is set by the parent AFTER the POST succeeds, so a failed confirm leaves the
            // buttons usable instead of collapsing the card on a write that never happened.
            setIsConfirmSubmitting(true);
            try {
              await onAction?.(
                `optisigns_write_confirm:${confirm.opId}`,
                messageId || "",
              );
            } finally {
              setIsConfirmSubmitting(false);
            }
          }}
          onCancel={() => {
            setLocalClickedActions((prev) => [...prev, cancelActionId]);
            onAction?.(cancelActionId, messageId || "");
          }}
        />
      </div>
    );
  }

  // Filter out thoughts from activity list (shown separately at top)
  const nonThoughtActivities = payload.activity.filter(
    (a) => a.type !== "thought",
  );
  const activityToShow = showAllActivity
    ? nonThoughtActivities
    : nonThoughtActivities.slice(0, 5);
  const hasMoreActivity = nonThoughtActivities.length > 5;

  // Calculate total thought time
  const totalThoughtTime = payload.activity
    .filter((a) => a.type === "thought")
    .reduce((sum, a) => sum + (a.duration_seconds || 0), 0);

  const hasChanges = payload.edits_count > 0;

  return (
    <div
      className={cn(
        "bg-card border border-border rounded-lg p-4 space-y-4",
        className,
      )}
    >
      {/* Total thought time - in pill */}
      {totalThoughtTime > 0 && (
        <div className="inline-flex items-center gap-1.5 text-muted-foreground text-xs bg-muted px-2.5 py-1 rounded-lg w-fit">
          <Lightbulb className="w-3.5 h-3.5" />
          <span>
            Thought for {totalThoughtTime} second
            {totalThoughtTime > 1 ? "s" : ""}
          </span>
        </div>
      )}

      {/* Markdown content - the actual response text */}
      {markdown && markdown.trim() && (
        <MarkdownRenderer
          content={markdown}
          isStreaming={false}
          className="text-sm"
        />
      )}

      {/* Activity Timeline - tools used in pill, files as plain text */}
      {nonThoughtActivities.length > 0 && (
        <div className="space-y-3">
          {/* Tools used pill */}
          <div className="inline-flex items-center gap-1.5 text-muted-foreground text-xs bg-muted px-2.5 py-1 rounded-lg w-fit">
            <Lightbulb className="w-3.5 h-3.5" />
            <span>
              {nonThoughtActivities.length} tool
              {nonThoughtActivities.length > 1 ? "s" : ""} used
            </span>
          </div>

          {/* Activity list - plain text with Hide button */}
          {(showAllActivity || !hasMoreActivity) && (
            <div className="space-y-2">
              {activityToShow.map((activity, index) => (
                <ActivityChip key={index} activity={activity} />
              ))}
            </div>
          )}

          {/* Hide button if more activities */}
          {hasMoreActivity && (showAllActivity || !hasMoreActivity) && (
            <button
              onClick={() => setShowAllActivity(!showAllActivity)}
              className="text-muted-foreground hover:text-foreground text-xs"
            >
              {showAllActivity ? "Hide" : "Show all"}
            </button>
          )}
        </div>
      )}

      {/* Changes section - pill with clickable expand */}
      {hasChanges && (
        <div className="space-y-3">
          {/* Edits made pill with inline Hide button */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => setShowAllChanges(!showAllChanges)}
              className="inline-flex items-center gap-1.5 text-muted-foreground text-xs bg-muted px-2.5 py-1 rounded-lg hover:bg-accent transition-colors"
            >
              <FileEdit className="w-3.5 h-3.5" />
              <span>
                {payload.edits_count} edit{payload.edits_count > 1 ? "s" : ""}{" "}
                made
              </span>
            </button>
            {payload.changes.length > 0 && showAllChanges && (
              <button
                onClick={() => setShowAllChanges(false)}
                className="text-muted-foreground hover:text-foreground text-xs"
              >
                Hide
              </button>
            )}
          </div>

          {/* Changed files list - clickable to open in VS Code */}
          {showAllChanges && payload.changes.length > 0 && (
            <div className="space-y-1.5">
              {payload.changes.map((change, index) => (
                <button
                  key={index}
                  onClick={() => onOpenFile?.(change.file)}
                  className="text-xs text-muted-foreground hover:text-foreground truncate text-left w-full transition-colors"
                  title={`Open ${change.file} in VS Code`}
                >
                  {change.file}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Action buttons - only show if there are edits */}
      {/* {hasChanges && (
        <div className="flex items-center justify-end pt-2">
          <div className="flex items-center gap-2">
            {onRestore && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onRestore}
                className="text-gray-400 hover:text-gray-200 h-7 px-2.5 text-xs"
              >
                <RotateCcw className="w-3 h-3 mr-1.5" />
                Restore
              </Button>
            )}
            {onViewCode && (
              <Button
                variant="contrast"
                size="sm"
                onClick={onViewCode}
                className="bg-blue-600 hover:bg-blue-700 h-7 px-2.5 text-xs"
              >
                <Code className="w-3 h-3 mr-1.5" />
                Code
              </Button>
            )}
          </div>
        </div>
      )} */}

      {/* Design notes */}
      {payload.notes.length > 0 && (
        <div className="space-y-2 text-xs text-muted-foreground italic">
          {payload.notes.map((note, index) => (
            <div key={index}>• {note}</div>
          ))}
        </div>
      )}

      {/* HITL Action buttons - rendered from payload.actions with id and label */}
      {payload.actions &&
        payload.actions.filter((a) => a.id && a.label).length > 0 && (
          <div className="flex justify-end gap-2 pt-2">
            {payload.actions
              .filter(
                (action): action is Action & { id: string; label: string } =>
                  Boolean(action.id && action.label),
              )
              .map((action) => {
                const isClicked =
                  localClickedActions.includes(action.id) ||
                  clickedActions.includes(action.id);
                return (
                  <Button
                    key={action.id}
                    variant={
                      isClicked ? "secondary" : action.variant || "default"
                    }
                    size="sm"
                    onClick={() => {
                      setLocalClickedActions((prev) => [...prev, action.id]);
                      onAction?.(action.id, messageId || "");
                    }}
                    disabled={isClicked}
                    className={cn(
                      "h-8 px-3 text-xs",
                      isClicked &&
                        "bg-muted text-muted-foreground cursor-default",
                    )}
                  >
                    {!isClicked && action.icon === "cloud" && (
                      <Cloud className="w-3.5 h-3.5 mr-1.5" />
                    )}
                    {!isClicked && action.icon === "play" && (
                      <Play className="w-3.5 h-3.5 mr-1.5" />
                    )}
                    {isClicked ? "Approved" : action.label}
                  </Button>
                );
              })}
          </div>
        )}
    </div>
  );
}

// Activity chip component
function ActivityChip({ activity }: { activity: ActivityEvent }) {
  const getIcon = () => {
    switch (activity.type) {
      case "thought":
        return <Lightbulb className="w-3.5 h-3.5" />;
      case "read":
        return <FileText className="w-3.5 h-3.5" />;
      case "write":
      case "edit":
        return <FileEdit className="w-3.5 h-3.5" />;
      default:
        return <FileText className="w-3.5 h-3.5" />;
    }
  };

  // Don't render thought activities - they're shown at the top
  if (activity.type === "thought") {
    return null;
  }

  const getLabel = (): string => {
    if (activity.path) {
      return activity.path;
    }
    return "";
  };

  const getTypeLabel = (): string => {
    switch (activity.type) {
      case "read":
        return "Read";
      case "write":
        return "Write";
      case "edit":
        return "Edit";
      default:
        return activity.type;
    }
  };

  return (
    <div className="flex items-center gap-2 text-muted-foreground text-xs">
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {getIcon()}
        <span>{getTypeLabel()}</span>
      </div>
      <span className="text-xs text-muted-foreground truncate">{getLabel()}</span>
    </div>
  );
}
